/**
 * TallyExportScreen — one-click export of Sales + Purchase vouchers as a
 * Tally-importable XML file. This is the CA hand-off: a month of vouchers the
 * accountant imports into Tally in one step instead of re-keying every bill.
 *
 * Plan-gated behind Data Export (PRO); permission-gated behind reports:export.
 */

import { useCallback, useEffect, useState } from 'react';
import { FileCode, RefreshCw, Info } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { usePlanGate, FEATURE_DATA_EXPORT } from '@/plans';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { startOfDay, toDateInputValue, fromDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { file } from '@/platform';
import { loadTallyRange, type TallyRangeResult } from '@/reports/tally/tallyDataSource';
import { buildTallyXml } from '@/reports/tally/tallyExport';

type Preset = 'month' | 'lastMonth' | 'custom';

function endOfDay(d: Date): Date { const c = new Date(d); c.setHours(23, 59, 59, 999); return c; }

function presetRange(preset: Preset, custom: [Date, Date]): [Date, Date] {
  const now = new Date();
  if (preset === 'month') {
    return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now)];
  }
  if (preset === 'lastMonth') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return [startOfDay(from), endOfDay(to)];
  }
  return custom;
}

export function TallyExportScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const { canAccess } = usePlanGate();
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1))));
  const [customTo, setCustomTo] = useState(toDateInputValue(new Date()));
  const [result, setResult] = useState<TallyRangeResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const range = useCallback((): [Date, Date] => {
    return presetRange(preset, [fromDateInputValue(customFrom), endOfDay(fromDateInputValue(customTo))]);
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      setResult(await loadTallyRange(activeStoreId, from, to));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const handleExport = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const xml = buildTallyXml({
        companyName: result.companyName,
        sales: result.sales,
        purchases: result.purchases,
      });
      const [from, to] = range();
      await file.saveText({
        content: xml,
        defaultFileName: `Tally_${toDateInputValue(from)}_${toDateInputValue(to)}.xml`,
        filters: [{ name: 'Tally XML', extensions: ['xml'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  if (!canAccess(FEATURE_DATA_EXPORT)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-heading text-fg-primary">Tally Export requires the Pro plan</div>
          <div className="mt-1 text-body text-fg-tertiary">Upgrade to Professional or Enterprise to export vouchers to Tally.</div>
        </div>
      </div>
    );
  }

  const totalVouchers = result ? result.salesCount + result.purchaseCount : 0;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['month', 'lastMonth', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'month' ? 'This month' : p === 'lastMonth' ? 'Last month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="primary" onClick={() => void handleExport()} disabled={!result || totalVouchers === 0 || exporting}>
            <FileCode size={14} className="mr-1.5" /> {exporting ? 'Exporting…' : 'Export Tally XML'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !result ? null : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label="Sales vouchers" value={String(result.salesCount)} />
            <Stat label="Purchase vouchers" value={String(result.purchaseCount)} />
            <Stat label="Company" value={result.companyName} small />
          </div>

          {totalVouchers === 0 ? (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-8 text-center text-body text-fg-tertiary">
              No vouchers in this period.
            </div>
          ) : (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
              <div className="mb-2 text-label uppercase text-fg-tertiary">Preview totals</div>
              <table className="w-full text-body">
                <tbody>
                  <PreviewRow label="Sales — taxable" value={formatCurrency(result.sales.reduce((s, v) => s + (v.isCreditNote ? -v.taxable : v.taxable), 0))} />
                  <PreviewRow label="Sales — tax" value={formatCurrency(result.sales.reduce((s, v) => { const t = v.cgst + v.sgst + v.igst + v.cess; return s + (v.isCreditNote ? -t : t); }, 0))} />
                  <PreviewRow label="Purchases — taxable" value={formatCurrency(result.purchases.reduce((s, v) => s + v.taxable, 0))} />
                  <PreviewRow label="Purchases — tax" value={formatCurrency(result.purchases.reduce((s, v) => s + v.cgst + v.sgst + v.igst, 0))} />
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-stroke-hairline/10 bg-bg-subtle/40 px-4 py-3 text-label text-fg-tertiary">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Import in Tally via <b>Gateway of Tally → Import Data → Vouchers</b>. Ledgers
              (Sales, Purchase, Output/Input CGST/SGST/IGST, Round Off) and party names are
              created on import if they don’t already exist. Verify GST ledgers map to your
              tax classifications after the first import.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, small }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 tabular-nums text-fg-primary', small ? 'text-body truncate' : 'text-heading')}>{value}</div>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-stroke-hairline/10 last:border-0">
      <td className="py-1.5 text-fg-secondary">{label}</td>
      <td className="py-1.5 text-right text-fg-primary tabular-nums">{value}</td>
    </tr>
  );
}
